import React, { useEffect, useMemo, useState, useContext } from "react";
import {
  Controller,
  useWatch,
  useForm,
  FormProvider,
  type FieldError,
} from "react-hook-form";
import "./UserActions.scss";
import { useTranslation } from "react-i18next";
import {
  userActions,
  type UserAction,
  type UserSubmitHandler,
} from "../UsersListUtils";
import { EMAIL_PATTERN_REGEX } from "../../../../Forms/FormValidations";
import UserEditableFields from "../../../UserProfile/UserEditableFields/UserEditableFields";
import AllowedScopes from "./AllowedScopes/AllowedScopes";
import {
  CheckBox,
  TagsSelect,
  TextButton,
  TextInput,
  SelectInput,
  ModalContext,
  ModalButtonsContainer,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import type { SelectOption } from "@SeemplicityDev/shared/src/design-system/controls/SelectInput/SelectInput";
import type { TagOption } from "@SeemplicityDev/shared/src/design-system/controls/TagsSelect/TagsSelect";
import useRoles from "../../useRoles";
// Reuse the canonical user/profile shapes from UsersList so the table and this
// form cannot diverge.
import type { UserProfile, UserRole } from "../UsersList";

interface UserActionsData {
  email?: string;
  firstName?: string;
  lastName?: string;
  id?: string;
  role?: UserRole;
  profiles?: UserProfile[];
  visible?: boolean;
}

interface UserActionsFormValues {
  email: string | null;
  firstName?: string;
  lastName?: string;
  role?: { value?: string; label?: string };
  profiles?: UserProfile[];
  withoutEmail?: boolean;
  password?: string;
  visible?: boolean;
  // `visibleUser` is referenced for errors only (never registered as a field);
  // kept so `errors.visibleUser` type-checks while preserving exact behavior.
  visibleUser?: never;
}

export interface UserActionsProps {
  mode: UserAction;
  data?: UserActionsData | null;
  onSubmit: UserSubmitHandler;
  profilesSuggestions?: UserProfile[];
  allowedDomains?: string[];
  isCNC?: boolean;
  isSamlUser?: boolean;
}

// Adapts a React Hook Form FieldError to the design-system CheckBox `error`
// shape ({ message, type }). Returns undefined when there is no error, matching
// the original `... ? error : null` behavior (falsy = no error rendered).
const toCheckBoxError = (
  fieldError?: FieldError
): { message: string; type: string } | undefined =>
  fieldError
    ? { message: fieldError.message ?? "", type: fieldError.type }
    : undefined;

const UserActions: React.FC<UserActionsProps> = ({
  mode,
  data = null,
  onSubmit,
  profilesSuggestions,
  allowedDomains,
  isCNC = false,
  isSamlUser,
}) => {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [isAllowedScopesOpen, setIsAllowedScopesOpen] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isMailOnBlur, setIsMailOnBlur] = useState(false);
  const methods = useForm<UserActionsFormValues>({
    mode: "onChange",
  });
  const {
    control,
    handleSubmit,
    reset: formReset,
    formState: { errors, isValid, isDirty },
  } = methods;
  const withoutEmail = useWatch({
    control,
    name: `withoutEmail`,
    defaultValue: false,
  });

  const { roles } = useRoles();

  const selectedProfiles = useWatch({ control, name: "profiles" });

  useEffect(() => {
    if (!!data && mode === userActions.EDIT) {
      formReset({
        email: data?.email,
        firstName: data?.firstName,
        lastName: data?.lastName,
        role: { value: data?.role?.id, label: data?.role?.name },
        profiles: data?.profiles,
        ...(isCNC && { visible: data?.visible }),
      });
    }
  }, [mode, data, isCNC]);

  const applySubmission = (formData: UserActionsFormValues) => {
    setIsSubmittingForm(true);

    // Boundary cast: the strongly-typed form values become the canonical opaque
    // submit payload (Record<string, unknown>) consumed by the parent handlers.
    const payload = (
      mode === userActions.EDIT ? { ...formData, id: data!.id } : formData
    ) as Record<string, unknown>;

    onSubmit(payload).then(() => {
      setIsSubmittingForm(false);
    });
  };

  const allowedScopesDisabled = useMemo(
    () =>
      !selectedProfiles?.length ||
      !selectedProfiles?.some((profile) => profile.hasScopes),
    [selectedProfiles]
  );

  return (
    <FormProvider {...methods}>
      {!isAllowedScopesOpen ? (
        <form
          className={"user-actions-form"}
          onSubmit={handleSubmit(applySubmission)}
        >
          <div>
            <div className="user-actions-form-content">
              <Controller
                name={"email"}
                defaultValue={null}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextInput
                    placeholder={"Type email"}
                    type={"email"}
                    label={"Email"}
                    required
                    labelTop
                    error={
                      isMailOnBlur && errors && errors.email
                        ? errors.email
                        : null
                    }
                    value={value ?? undefined}
                    disabled={mode === userActions.EDIT}
                    onChange={(val: React.ChangeEvent<HTMLInputElement>) =>
                      onChange(val)
                    }
                    onBlur={() => {
                      setIsMailOnBlur(true);
                      onBlur();
                    }}
                    data-testid={"user-actions-email"}
                  />
                )}
                control={control}
                rules={{
                  required: REQUIRED_ERROR_MESSAGE,
                  validate: {
                    validEmail: (value) => {
                      const isValid =
                        EMAIL_PATTERN_REGEX.test(value ?? "") || isSamlUser;
                      return isValid || t("Invalid Email");
                    },
                    allowedDomains: (value) => {
                      if (mode !== userActions.EDIT) {
                        const domain = value?.substring(
                          value?.lastIndexOf("@") + 1
                        );

                        if (
                          domain &&
                          allowedDomains?.find(
                            (allowedDomain) => allowedDomain === domain
                          )
                        ) {
                          return true;
                        }

                        return t(
                          "User’s domain is not allowed, change the domain or contact support"
                        );
                      } else {
                        return true;
                      }
                    },
                  },
                }}
              />
              <div className={"user-actions-inner-wrap"}>
                <UserEditableFields isDisabled={false} />
                <Controller
                  name={"role"}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <SelectInput
                      data-testid={"user-role"}
                      name={"role"}
                      value={value as SelectOption | undefined}
                      onBlur={onBlur}
                      labelTop
                      onChange={(selectedValue: {
                        value?: string;
                        label?: string;
                      }) => onChange(selectedValue)}
                      error={errors?.role ? errors.role : undefined}
                      placeholder={"Assign a user Role"}
                      isSearchable={true}
                      label={"Role"}
                      options={roles}
                      required
                    />
                  )}
                  control={control}
                  rules={{ required: REQUIRED_ERROR_MESSAGE }}
                />
              </div>
              <Controller
                name={"profiles"}
                // main defaults this field to an empty string; preserved exactly.
                // Once tags are selected the field holds UserProfile[]/TagOption[].
                defaultValue={"" as unknown as UserProfile[]}
                render={({ field: { onChange, value, onBlur } }) => (
                  <TagsSelect
                    data-testid={`user-profiles`}
                    onChange={(tags: TagOption[]) => onChange(tags)}
                    value={(value || "") as unknown as TagOption[] | undefined}
                    placeholder={`Search Profiles...`}
                    suggestions={profilesSuggestions as unknown as TagOption[]}
                    label={"Profiles"}
                    error={
                      (errors?.profiles ? errors?.profiles : undefined) as
                        | { message?: string; type?: string }
                        | undefined
                    }
                    labelTop
                    required
                    onBlur={onBlur}
                    name={"profiles"}
                    minQueryLength={0}
                  />
                )}
                control={control}
                rules={{ required: REQUIRED_ERROR_MESSAGE }}
              />

              <TextButton
                buttonStyle={"textLink"}
                disabled={allowedScopesDisabled}
                label={t(
                  `${
                    allowedScopesDisabled ? "No" : "View"
                  } Allowed Groups & Scopes`
                )}
                data-testid={"allowed-scopes"}
                type={"button"}
                onClick={() => setIsAllowedScopesOpen(true)}
              />

              {isCNC && (
                <>
                  {mode === userActions.CREATE && (
                    <>
                      <Controller
                        name={"withoutEmail"}
                        render={({ field: { value, onChange } }) => {
                          return (
                            <CheckBox
                              name={"withoutEmail"}
                              data-testid={"withoutEmail"}
                              checked={value}
                              className={"manage-user-cb"}
                              label={
                                "Do not send an invitation email to the user"
                              }
                              onChange={(val, newValue) => {
                                onChange(newValue);
                              }}
                              error={toCheckBoxError(errors.withoutEmail)}
                            />
                          );
                        }}
                        control={control}
                      />
                      {withoutEmail && (
                        <Controller
                          shouldUnregister={true}
                          name={"password"}
                          render={({ field: { value, onChange, onBlur } }) => {
                            return (
                              <TextInput
                                type={"text"}
                                value={value}
                                data-testid={"password"}
                                labelTop
                                onBlur={onBlur}
                                name={"password"}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  onChange(e);
                                }}
                                label={"Password"}
                                error={errors.password ? errors.password : null}
                              />
                            );
                          }}
                          rules={{
                            required: REQUIRED_ERROR_MESSAGE,
                          }}
                          control={control}
                        />
                      )}
                    </>
                  )}
                  <Controller
                    name={"visible"}
                    defaultValue={true}
                    render={({ field: { value, onChange } }) => {
                      return (
                        <CheckBox
                          name={"visibleUser"}
                          data-testid={"visibleUser"}
                          checked={value}
                          className={"manage-user-cb"}
                          label={"Visible User"}
                          onChange={(val, newValue) => {
                            onChange(newValue);
                          }}
                          error={toCheckBoxError(errors.visibleUser)}
                        />
                      );
                    }}
                    control={control}
                  />
                </>
              )}
            </div>
          </div>
          <ModalButtonsContainer>
            <TextButton
              type="button"
              data-testid={"user-actions-cancel"}
              label={"Cancel"}
              onClick={() => closeModal()}
            />
            <TextButton
              data-testid={"user-actions-submit"}
              type="submit"
              label={"Save"}
              buttonStyle={"primary"}
              isSubmitting={isSubmittingForm}
              rightIcon={isSubmittingForm ? "seem-icon-spinner spinner" : ""}
              disabled={!isValid || !isDirty || isSubmittingForm}
            />
          </ModalButtonsContainer>
        </form>
      ) : (
        <div className={"user-actions-form"}>
          <AllowedScopes
            onClosingAllowedScopes={() => setIsAllowedScopesOpen(false)}
            profiles={selectedProfiles}
          />
        </div>
      )}
    </FormProvider>
  );
};
export default UserActions;
